'use client';

import {
  SettingsCard,
  SettingsRow,
  SettingsSection,
  toasterApi,
} from '@terra/ui-design-system';
import { VStack } from '@terra/ui-styled-system';
import type {
  AiApplicationMcpTransport,
  UpdateAiApplicationDto,
} from '@terra/shared-contracts-ai-applications';
import type {
  McpAuthMode,
  UpsertMcpCredentialDto,
} from '@terra/shared-contracts-mcp-servers';
import {
  finalizeMcpConnectionAfterSave,
  resolveInitialMcpAuthMode,
  resolveMcpDraftSyncOptions,
} from '@terra/shared-contracts-mcp-servers';
import {
  getMcpCredentialAuthTypeLabel,
  getMcpTransportLabel,
  validateAiApplicationCreateUrl,
} from '@terra/ui-ai-applications';
import { Edit03Icon, PlayIcon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAiApplication } from '../../hooks/api/useAiApplication';
import { useUpdateAiApplication } from '../../hooks/api/useAiApplicationMutations';
import { useAiApplicationUrlEditRestriction } from '../../hooks/api/useAiApplicationUrlEditRestriction';
import {
  useAuthorizeTenantMcpServer,
  useCompleteMcpOAuthCallback,
  useTenantMcpServer,
  useUpsertTenantMcpCredential,
} from '../../hooks/api/useMcpServers';
import {
  useMcpServerIdentities,
  useUpdateMcpServerIdentity,
} from '../../hooks/api/useMcpServerIdentities';
import { useProxyStore } from '../../store/proxy-store';
import { McpServerIdentitiesSection } from './mcp-connection/McpServerIdentitiesSection';
import { electronMcpOAuthStrategy } from './mcp-connection/electronMcpOAuth';
import { McpConnectionEditDialog } from './mcp-connection/McpConnectionEditDialog';
import { useMcpConnectionTesting } from './mcp-connection/useMcpConnectionTesting';

type AiApplicationMcpConnectionTabProps = {
  aiApplicationId: string;
  isReadOnly?: boolean;
};

export const AiApplicationMcpConnectionTab = ({
  aiApplicationId,
  isReadOnly,
}: AiApplicationMcpConnectionTabProps) => {
  const tAi = useTranslations('aiApplications');
  const tAiSettings = useTranslations('aiApplications.settings');
  const tCommon = useTranslations('common');
  const tAdminMcp = useTranslations('settings.mcpServerForm');
  const { aiApplication, isLoading } = useAiApplication({
    aiApplicationId,
  });
  const { canEditUrl, urlEditRestrictionReason } =
    useAiApplicationUrlEditRestriction({
      aiApplicationId,
    });
  const updateAiApplicationMutation = useUpdateAiApplication({
    aiApplicationId,
  });
  const {
    identityConnectionStatus,
    isTestPending,
    pendingTestIdentityId,
    isBulkConnectionTestPending,
    testConnection,
    applyConnectionTestResult,
    showConnectionTestSuccess,
    showConnectionTestError,
    handleTestConnection,
    handleTestIdentity,
  } = useMcpConnectionTesting({ aiApplicationId });
  const authorizeMcpServerMutation = useAuthorizeTenantMcpServer();
  const upsertCredentialMutation = useUpsertTenantMcpCredential();
  const completeOAuthCallbackMutation = useCompleteMcpOAuthCallback();
  const { server: mcpServer } = useTenantMcpServer({
    id: aiApplication?.mcpServerId ?? '',
  });
  const { identities: mcpIdentities } = useMcpServerIdentities({
    serverId: aiApplication?.mcpServerId ?? '',
  });
  const updateIdentityMutation = useUpdateMcpServerIdentity();
  const mcpConnectionEditRequest = useProxyStore(
    (state) => state.mcpConnectionEditRequest,
  );
  const setMcpConnectionEditRequest = useProxyStore(
    (state) => state.setMcpConnectionEditRequest,
  );
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [draftTransport, setDraftTransport] =
    useState<AiApplicationMcpTransport>('HTTP');
  const [draftUrl, setDraftUrl] = useState('');
  const [draftUrlError, setDraftUrlError] = useState('');
  const [draftCredential, setDraftMcpCredential] =
    useState<UpsertMcpCredentialDto | null>(null);
  const [draftCredentialLabel, setDraftCredentialLabel] = useState('');
  const [mcpAuthMode, setMcpAuthMode] = useState<McpAuthMode>('oauth');
  const [isSaving, setIsSaving] = useState(false);
  const [isOAuthOnlyEdit, setIsOAuthOnlyEdit] = useState(false);
  const [pendingIdentityEditId, setPendingIdentityEditId] = useState<
    string | null
  >(null);
  const hasManagedIdentities = Boolean(aiApplication?.mcpServerId);
  const openDefaultIdentityEdit = useCallback(() => {
    const defaultIdentity =
      mcpIdentities.find((identity) => identity.isDefault) ?? mcpIdentities[0];

    if (defaultIdentity) {
      setPendingIdentityEditId(defaultIdentity.id);
    }
  }, [mcpIdentities]);
  const mcpOAuthMessages = useMemo(
    () => ({
      oauthPopupBlocked: tAdminMcp('oauthPopupBlocked'),
      oauthAuthorizationFailed: tAdminMcp('oauthAuthorizationFailed'),
      oauthAuthorizationTimedOut: tAdminMcp('oauthAuthorizationTimedOut'),
      oauthAuthorizationWindowClosed: tAdminMcp(
        'oauthAuthorizationWindowClosed',
      ),
      oauthAuthorizationIncomplete: tAdminMcp('oauthAuthorizationIncomplete'),
    }),
    [tAdminMcp],
  );
  const getDiscoveryStatusLabel = (
    status: NonNullable<typeof aiApplication>['discoveryStatus'],
  ) => tAiSettings(`general.discoveryStatusValues.${status}`);
  const getTransportLabel = (
    transport: AiApplicationMcpTransport | null | undefined,
  ) =>
    getMcpTransportLabel({
      transport,
      translate: (key) => tAi(key),
      emptyValue: tAi('emptyValue'),
    });
  const validateDraftUrl = (url: string) =>
    url.trim()
      ? validateAiApplicationCreateUrl({
          url,
          urlErrorMessage: tAi('fields.urlInvalid'),
        })
      : tAi('fields.urlInvalid');
  const showMcpUrlEditAction = !isReadOnly && canEditUrl;
  const showMcpTransportEditAction = !isReadOnly && !canEditUrl;
  const openEditDialog = useCallback(
    ({
      oauthOnly = false,
    }: {
      oauthOnly?: boolean;
    } = {}) => {
      if (oauthOnly && hasManagedIdentities) {
        openDefaultIdentityEdit();

        return;
      }

      setDraftTransport(aiApplication?.mcpTransport ?? 'HTTP');
      setDraftUrl(aiApplication?.url ?? '');
      setDraftUrlError('');
      setDraftMcpCredential(null);
      const defaultIdentity =
        mcpIdentities.find((identity) => identity.isDefault) ??
        mcpIdentities[0];

      setDraftCredentialLabel(defaultIdentity?.label ?? '');
      setMcpAuthMode(
        oauthOnly
          ? 'oauth'
          : resolveInitialMcpAuthMode({
              discoveryMode: aiApplication?.discoveryMode,
              hasConfiguredMcpCredential: mcpIdentities.some(
                (identity) => identity.type !== 'NONE',
              ),
            }),
      );
      setIsOAuthOnlyEdit(oauthOnly);
      setIsEditOpen(true);
    },
    [
      aiApplication?.discoveryMode,
      aiApplication?.mcpTransport,
      aiApplication?.url,
      hasManagedIdentities,
      mcpIdentities,
      openDefaultIdentityEdit,
    ],
  );
  const connectionEditAction = useMemo(
    () => ({
      label: tCommon('edit'),
      icon: <Edit03Icon size={16} />,
      onClick: () => openEditDialog(),
    }),
    [openEditDialog, tCommon],
  );

  useEffect(() => {
    if (
      mcpConnectionEditRequest?.applicationId !== aiApplicationId ||
      !mcpConnectionEditRequest.openEdit
    ) {
      return;
    }

    const { identityId, oauthOnly } = mcpConnectionEditRequest;

    setMcpConnectionEditRequest(null);
    if (isReadOnly) {
      return;
    }

    if (identityId && aiApplication?.mcpServerId) {
      setPendingIdentityEditId(identityId);

      return;
    }

    openEditDialog({ oauthOnly: oauthOnly ?? false });
  }, [
    aiApplication?.mcpServerId,
    aiApplicationId,
    isReadOnly,
    mcpConnectionEditRequest,
    openEditDialog,
    setMcpConnectionEditRequest,
  ]);
  const handleSaveConnection = async () => {
    if (canEditUrl) {
      const urlError = validateDraftUrl(draftUrl);

      if (urlError) {
        setDraftUrlError(urlError);

        return;
      }
    }

    const connectionApplicationFields = {
      mcpTransport: draftTransport,
      ...(canEditUrl ? { url: draftUrl.trim() } : {}),
    };

    if (hasManagedIdentities) {
      setIsSaving(true);

      try {
        await updateAiApplicationMutation.mutateAsync(
          connectionApplicationFields,
        );
        toasterApi.success({ title: tAiSettings('saveSuccess') });
        setIsEditOpen(false);
      } catch (error: unknown) {
        toasterApi.error({
          title: tAiSettings('saveError'),
          description: error instanceof Error ? error.message : undefined,
        });
      } finally {
        setIsSaving(false);
      }

      return;
    }

    setIsSaving(true);

    try {
      const defaultIdentity =
        mcpIdentities.find((identity) => identity.isDefault) ??
        mcpIdentities[0];
      const trimmedLabel = draftCredentialLabel.trim();
      const credentialWithLabel =
        draftCredential && trimmedLabel
          ? { ...draftCredential, label: trimmedLabel }
          : draftCredential;
      const mcpServerId = aiApplication?.mcpServerId ?? undefined;

      if (mcpAuthMode === 'manualCredentials') {
        await updateAiApplicationMutation.mutateAsync({
          ...connectionApplicationFields,
          ...resolveMcpDraftSyncOptions({ mcpAuthMode }),
        });
        toasterApi.success({ title: tAiSettings('saveSuccess') });
        setIsEditOpen(false);

        return;
      }

      const labelChanged =
        Boolean(mcpServerId) &&
        Boolean(defaultIdentity) &&
        trimmedLabel.length > 0 &&
        trimmedLabel !== defaultIdentity?.label;
      const urlChanged =
        canEditUrl && draftUrl.trim() !== (aiApplication?.url ?? '').trim();
      const transportChanged =
        draftTransport !== (aiApplication?.mcpTransport ?? 'HTTP');
      const connectionChanged =
        Boolean(credentialWithLabel) || urlChanged || transportChanged;

      if (
        labelChanged &&
        !connectionChanged &&
        mcpServerId &&
        defaultIdentity
      ) {
        await updateIdentityMutation.mutateAsync({
          serverId: mcpServerId,
          identityId: defaultIdentity.id,
          data: { label: trimmedLabel },
        });
        toasterApi.success({ title: tAiSettings('saveSuccess') });
        setIsEditOpen(false);

        return;
      }

      const body: UpdateAiApplicationDto = {
        ...connectionApplicationFields,
        ...(credentialWithLabel ? { mcpCredential: credentialWithLabel } : {}),
      };
      const updated = await updateAiApplicationMutation.mutateAsync(body);

      if (
        labelChanged &&
        !credentialWithLabel &&
        mcpServerId &&
        defaultIdentity
      ) {
        await updateIdentityMutation.mutateAsync({
          serverId: mcpServerId,
          identityId: defaultIdentity.id,
          data: { label: trimmedLabel },
        });
      }

      const finalizeResult = await finalizeMcpConnectionAfterSave({
        mcpCredential: credentialWithLabel,
        mcpServerId: updated?.mcpServerId ?? aiApplication?.mcpServerId,
        upsertCredential:
          credentialWithLabel &&
          (updated?.mcpServerId ?? aiApplication?.mcpServerId)
            ? async ({ serverId, credential }) => {
                await upsertCredentialMutation.mutateAsync({
                  id: serverId,
                  credential,
                });
              }
            : undefined,
        fetchAuthorizeUrl: (serverId) =>
          authorizeMcpServerMutation.mutateAsync(serverId),
        completeOAuthCallback: async ({ code, state }) => {
          await completeOAuthCallbackMutation.mutateAsync({ code, state });
        },
        testConnection,
        messages: mcpOAuthMessages,
        onOAuthError: ({ title, description }) => {
          toasterApi.error({ title, description });
        },
        onTestResult: applyConnectionTestResult,
        onTestSuccess: showConnectionTestSuccess,
        onTestError: showConnectionTestError,
        oauthStrategy: electronMcpOAuthStrategy,
      });

      if (finalizeResult.oauthRequired && !finalizeResult.oauthAuthorized) {
        return;
      }

      toasterApi.success({ title: tAiSettings('saveSuccess') });
      setIsEditOpen(false);
    } catch (error: unknown) {
      toasterApi.error({
        title: tAiSettings('saveError'),
        description: error instanceof Error ? error.message : undefined,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const displayedAuthLabel =
    mcpServer?.credentialType != null
      ? getMcpCredentialAuthTypeLabel({
          credentialType: mcpServer.credentialType,
          translate: tAdminMcp,
        })
      : tAi('emptyValue');

  return (
    <VStack alignItems="stretch" gap={8} w="full">
      <SettingsSection
        title={tAiSettings('mcpConnection.title')}
        description={tAiSettings('mcpConnection.sectionDescription')}
      >
        <SettingsCard isLoading={isLoading}>
          <SettingsRow
            label={tAiSettings('general.discoveryStatus')}
            value={
              aiApplication?.discoveryStatus
                ? getDiscoveryStatusLabel(aiApplication.discoveryStatus)
                : tAi('emptyValue')
            }
            isReadOnly
          />
          <SettingsRow
            label={tAi('fields.mcpTransport')}
            value={getTransportLabel(aiApplication?.mcpTransport)}
            action={
              showMcpTransportEditAction ? connectionEditAction : undefined
            }
            isReadOnly={!showMcpTransportEditAction}
          />
          <SettingsRow
            label={tAi('fields.mcpUrl')}
            value={aiApplication?.url ?? tAi('emptyValue')}
            action={showMcpUrlEditAction ? connectionEditAction : undefined}
            tooltip={canEditUrl ? undefined : urlEditRestrictionReason}
            isReadOnly={isReadOnly || !canEditUrl}
          />
          {aiApplication?.mcpServerId ? (
            <McpServerIdentitiesSection
              mcpServerId={aiApplication.mcpServerId}
              discoveryUrl={aiApplication.url ?? ''}
              isReadOnly={isReadOnly}
              connectionStatusByIdentityId={
                identityConnectionStatus ?? undefined
              }
              onTestIdentity={isReadOnly ? undefined : handleTestIdentity}
              testingIdentityId={pendingTestIdentityId}
              isBulkConnectionTestPending={isBulkConnectionTestPending}
              openEditIdentityId={pendingIdentityEditId}
              onOpenEditIdentityHandled={() => setPendingIdentityEditId(null)}
            />
          ) : (
            <SettingsRow
              label={tAdminMcp('fieldAuth')}
              value={displayedAuthLabel}
              action={isReadOnly ? undefined : connectionEditAction}
              isReadOnly={isReadOnly}
            />
          )}
          {!isReadOnly ? (
            <SettingsRow
              label={tAi('fields.testMcpConnection')}
              description={tAi('fields.testMcpConnectionHint')}
              action={{
                label: tAi('actions.testConnection'),
                icon: <PlayIcon size={16} />,
                onClick: handleTestConnection,
                disabled: isTestPending,
              }}
            />
          ) : null}
        </SettingsCard>
      </SettingsSection>

      <McpConnectionEditDialog
        open={isEditOpen}
        onOpenChange={(open) => {
          setIsEditOpen(open);
          if (!open) {
            setIsOAuthOnlyEdit(false);
          }
        }}
        onSubmit={() => {
          void handleSaveConnection();
        }}
        isSaving={isSaving}
        canEditUrl={canEditUrl}
        hasManagedIdentities={hasManagedIdentities}
        isOAuthOnlyEdit={isOAuthOnlyEdit}
        transport={draftTransport}
        onTransportChange={setDraftTransport}
        url={draftUrl}
        urlError={draftUrlError}
        onUrlChange={(nextUrl) => {
          setDraftUrl(nextUrl);
          setDraftUrlError(validateDraftUrl(nextUrl));
        }}
        authMode={mcpAuthMode}
        onAuthModeChange={(mode) => {
          setMcpAuthMode(mode);
          if (mode === 'manualCredentials') {
            setDraftMcpCredential(null);
          }
        }}
        credentialLabel={draftCredentialLabel}
        onCredentialLabelChange={setDraftCredentialLabel}
        mcpServerId={aiApplication?.mcpServerId ?? null}
        existingServer={mcpServer}
        onCredentialChange={setDraftMcpCredential}
      />
    </VStack>
  );
};
